import type { CreditLimitStatus, CreditRule } from '@shared/api';

/**
 * Pure helpers for the proposal credits admin screen (docs/limits.md
 * "Admin screen"). The screen shows one card per agency — its shared total,
 * its per-rep limit and any named-rep exceptions — and saves back to the
 * same flat rule list (and the same rule ids) the CLI writes.
 */

export type RuleWindow = CreditRule['window'];

export const WINDOW_OPTIONS: Array<{ value: RuleWindow; label: string }> = [
  { value: 'month', label: 'a month' },
  { value: 'week', label: 'a week' },
  { value: 'day', label: 'a day' },
  { value: 'none', label: 'total, never resets' },
];

/** One agency's limits: what the agency card shows and edits. */
export interface AgencyGroup {
  organizationId: string;
  /** The shared pool every rep in the agency draws from. */
  pool?: CreditRule;
  /** The default limit each rep in the agency gets. */
  perRep?: CreditRule;
  /** Named reps with their own limit in this agency (replaces perRep for them). */
  exceptions: CreditRule[];
}

export interface RuleGroups {
  agencies: AgencyGroup[];
  /** Limits not tied to one agency: each rep everywhere, and named reps in any agency. */
  everywhere: { perRep?: CreditRule; reps: CreditRule[] };
}

export function groupRules(rules: CreditRule[], nameOf: (id: string) => string | null = () => null): RuleGroups {
  const byOrg = new Map<string, AgencyGroup>();
  const group = (org: string) => {
    let found = byOrg.get(org);
    if (!found) {
      found = { organizationId: org, exceptions: [] };
      byOrg.set(org, found);
    }
    return found;
  };
  const everywhere: RuleGroups['everywhere'] = { reps: [] };
  for (const rule of rules) {
    const org = rule.organizationId;
    if (rule.scope === 'agency') {
      if (org) group(org).pool = rule;
    } else if (rule.username) {
      if (org) group(org).exceptions.push(rule);
      else everywhere.reps.push(rule);
    } else if (org) {
      group(org).perRep = rule;
    } else {
      everywhere.perRep = rule;
    }
  }
  const label = (g: AgencyGroup) => (nameOf(g.organizationId) ?? savedName(g) ?? g.organizationId).toLowerCase();
  return { agencies: [...byOrg.values()].sort((a, b) => label(a).localeCompare(label(b))), everywhere };
}

/** The agency name saved on one of its rules, for when the live lookup is down. */
export function savedName(group: AgencyGroup): string | null {
  return group.pool?.organizationName ?? group.perRep?.organizationName ?? group.exceptions[0]?.organizationName ?? null;
}

export function agencyLabel(id: string | undefined | null, name: string | undefined | null): string {
  if (!id) return 'No agency';
  return name ? name : id;
}

/** "3 a day", "10 a month", "20 total, never resets". */
export function describeAllowance(rule: Pick<CreditRule, 'credits' | 'window'>): string {
  if (rule.window === 'none') return `${rule.credits} total, never resets`;
  return `${rule.credits} a ${rule.window}`;
}

/** "12 of 100 used · resets Oct 1"; a never-resets total has no date. */
export function describeUsage(usage: CreditLimitStatus | null | undefined): string {
  if (!usage) return '';
  const resets = usage.resetsOn ? ` · resets ${formatDay(usage.resetsOn)}` : '';
  return `${usage.used} of ${usage.credits} used${resets}`;
}

/** "America/New_York" -> "Eastern Time". */
export function timezoneLabel(timeZone: string): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longGeneric' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    return part?.value ?? timeZone;
  } catch {
    return timeZone;
  }
}

export function formatDay(isoDay: string): string {
  return new Date(`${isoDay}T00:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
}

// --- the agency form -------------------------------------------------------

export interface AgencyDraft {
  organizationId: string;
  /** The looked-up name, saved on the rules so the card still reads if the lookup is down. */
  organizationName: string;
  /** Blank = no shared limit. */
  totalCredits: string;
  totalWindow: RuleWindow;
  /** Blank = no per-rep limit. */
  perRepCredits: string;
  perRepWindow: RuleWindow;
  note: string;
}

export function emptyAgencyDraft(): AgencyDraft {
  return { organizationId: '', organizationName: '', totalCredits: '', totalWindow: 'month', perRepCredits: '', perRepWindow: 'day', note: '' };
}

export function agencyDraftFrom(group: AgencyGroup, name: string | null): AgencyDraft {
  return {
    organizationId: group.organizationId,
    organizationName: name ?? savedName(group) ?? '',
    totalCredits: group.pool ? String(group.pool.credits) : '',
    totalWindow: group.pool?.window ?? 'month',
    perRepCredits: group.perRep ? String(group.perRep.credits) : '',
    perRepWindow: group.perRep?.window ?? 'day',
    note: group.pool?.note ?? group.perRep?.note ?? '',
  };
}

type Result = { rules: CreditRule[] } | { error: string };

function parseCredits(raw: string): number | null | 'bad' {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 ? n : 'bad';
}

/**
 * A rule saved over `existing`. A loaded (never-resets) balance keeps its
 * start date when edited, so raising it tops it up; a new one starts today.
 * A day/week/month limit counts everything already spent in its window.
 */
function buildRule(
  base: Omit<CreditRule, 'credits' | 'window' | 'startsAt' | 'note'>,
  credits: number,
  window: RuleWindow,
  note: string,
  existing: CreditRule | undefined,
  now: Date,
): CreditRule {
  const keepStart = existing?.window === 'none' ? existing.startsAt : undefined;
  return {
    ...base,
    credits,
    window,
    ...(window === 'none' ? { startsAt: keepStart ?? now.toISOString() } : {}),
    ...(note.trim() ? { note: note.trim() } : {}),
  };
}

/**
 * Saves an agency card: its pool (`agency-<org>`) and per-rep default
 * (`rep-default-<org>`), the CLI's ids. A blank field removes that limit.
 * `isNew` refuses an agency that already has limits.
 */
export function applyAgencyDraft(rules: CreditRule[], draft: AgencyDraft, isNew: boolean, now: Date = new Date()): Result {
  const org = draft.organizationId.trim();
  if (!org) return { error: 'Enter the agency id.' };
  const total = parseCredits(draft.totalCredits);
  const perRep = parseCredits(draft.perRepCredits);
  if (total === 'bad' || perRep === 'bad') return { error: 'Limits must be whole numbers, 0 or more.' };
  if (total === null && perRep === null) return { error: 'Set a total, a per-rep limit, or both.' };
  if (isNew && rules.some((r) => r.organizationId === org)) {
    return { error: 'That agency already has limits. Edit its card instead.' };
  }

  const name = draft.organizationName.trim();
  const named = { organizationId: org, ...(name ? { organizationName: name } : {}) };
  const poolId = `agency-${org}`;
  const perRepId = `rep-default-${org}`;
  const oldPool = rules.find((r) => r.id === poolId);
  const oldPerRep = rules.find((r) => r.id === perRepId);

  const next = rules.filter((r) => r.id !== poolId && r.id !== perRepId).map((r) =>
    // Exceptions carry the agency name too, so it reads the same everywhere.
    r.organizationId === org && name ? { ...r, organizationName: name } : r,
  );
  if (total !== null) {
    next.push(buildRule({ id: poolId, scope: 'agency', ...named }, total, draft.totalWindow, draft.note, oldPool, now));
  }
  if (perRep !== null) {
    next.push(buildRule({ id: perRepId, scope: 'rep', ...named }, perRep, draft.perRepWindow, draft.note, oldPerRep, now));
  }
  return { rules: next };
}

/** Every rule for the agency, gone: its reps become unlimited (or fall to the everywhere limit). */
export function removeAgency(rules: CreditRule[], organizationId: string): CreditRule[] {
  return rules.filter((r) => r.organizationId !== organizationId);
}

// --- the one-limit form (a named rep, or each rep everywhere) --------------

export interface LimitDraft {
  /** Blank on the "each rep, every agency" form. */
  username: string;
  credits: string;
  window: RuleWindow;
  note: string;
}

export function emptyLimitDraft(): LimitDraft {
  return { username: '', credits: '', window: 'day', note: '' };
}

export function limitDraftFrom(rule: CreditRule): LimitDraft {
  return { username: rule.username ?? '', credits: String(rule.credits), window: rule.window, note: rule.note ?? '' };
}

/**
 * Saves one rep's limit (`rep-<email>[-<org>]`) or, with `everyRep`, the
 * default for each rep everywhere (`rep-default`). Editing keeps the id.
 */
export function applyLimitDraft(
  rules: CreditRule[],
  draft: LimitDraft,
  target: { organizationId: string | null; organizationName?: string | null; everyRep?: boolean },
  editingId: string | null,
  now: Date = new Date(),
): Result {
  const credits = parseCredits(draft.credits);
  if (credits === null || credits === 'bad') return { error: 'The limit must be a whole number, 0 or more.' };
  const username = draft.username.trim().toLowerCase();
  if (!target.everyRep && !/^[^\s@]+@[^\s@]+$/.test(username)) return { error: "Enter the rep's sign-in email." };

  const org = target.everyRep ? null : target.organizationId;
  const existing = editingId ? rules.find((r) => r.id === editingId) : undefined;
  const id = existing?.id ?? (target.everyRep ? 'rep-default' : `rep-${username}${org ? `-${org}` : ''}`);
  if (!existing && rules.some((r) => r.id === id)) {
    return { error: target.everyRep ? 'That limit already exists. Edit it instead.' : 'That rep already has a limit here. Edit it instead.' };
  }
  const rule = buildRule(
    {
      id,
      scope: 'rep',
      ...(org ? { organizationId: org } : {}),
      ...(org && target.organizationName ? { organizationName: target.organizationName } : {}),
      ...(target.everyRep ? {} : { username }),
    },
    credits,
    draft.window,
    draft.note,
    existing,
    now,
  );
  return { rules: existing ? rules.map((r) => (r.id === existing.id ? rule : r)) : [...rules, rule] };
}
